/**
 * HOW MUCH A TESTED PLAN PUTS INTO A GUARANTEED INCOME — the one rule for every module (owner, 21 Oct 2026:
 * the village review's decision 11, applied across the board; docs/ux-review/modules/README.md).
 *
 * Up to the share of the invested money the client allows (a quarter by default), and — where it would lift
 * the Age Pension — the amount that lifts it most, as a plan of its own (shared/src/calculation/annuitySizing.ts).
 * The assets Centrelink counts after the move are estimated here only to SIZE that plan: the money being
 * invested, plus the savings, pensions, shares and investment properties already held, plus super once
 * someone has reached Age Pension age. The projection decides whether the plan actually does better.
 */
import type { DraftFactFind } from '../constants/initialFactFind.ts';
import { pensionMaximisingAnnuity, annuityTestAmounts } from '../../../shared/src/calculation/annuitySizing.ts';
import { annuityCeilingOf } from '../../../shared/src/calculation/annuityPreference.ts';
import { PENSION_AGE } from '../../../shared/src/calculation/constants/ratesAndThresholds.ts';
import { calculateAge } from '../utils/index.ts';
import { sumAssetType } from './fundingMechanisms.ts';

export interface AnnuitySize {
    amount: number;
    /** True for the amount that lifts the Age Pension most (when it is a different plan from the capped share). */
    pensionMax: boolean;
    /** The amount as a share of the money invested, rounded. */
    sharePct: number;
}

export interface AnnuitySizeInput {
    /** The money the plan invests (what is freed, or what is left after the move). */
    investable: number;
    /**
     * The module's share, 0–100 (its own older set-up answer, or 25). 0 means no guaranteed income is tested.
     * The client's own ceiling (annuityCeilingOf: a share or a dollar amount, asked once) wins when answered.
     */
    capPct: number;
    /** Whether the household owns its home after the move (the homeowner limits apply). */
    homeowner: boolean;
    /** Assets counted after the move that are not in the file today (e.g. a kept home that becomes assessable). */
    extraCounted?: number;
    /** Assets in the file today that the move spends or removes (e.g. savings paying an entry contribution). */
    lessCounted?: number;
}

export function annuitySizesFor(factFind: DraftFactFind | null | undefined, i: AnnuitySizeInput): AnnuitySize[] {
    if (!(i.capPct > 0) || !(i.investable > 0)) return [];
    const isCouple = factFind?.adviceFor === 'couple';
    const clientAge = factFind?.client?.dob ? calculateAge(factFind.client.dob) : null;
    const partnerAge = isCouple && factFind?.partner?.dob ? calculateAge(factFind.partner.dob) : null;
    const counted = i.investable
        + ['bankAccounts', 'termDeposits', 'shares', 'accountBasedPensions', 'investmentProperty'].reduce((t, k) => t + sumAssetType(factFind, k), 0)
        + ([clientAge, partnerAge].some((x) => x != null && x >= PENSION_AGE) ? sumAssetType(factFind, 'superannuation') : 0)
        + Math.max(0, i.extraCounted || 0)
        - Math.max(0, i.lessCounted || 0);
    const pensionMax = pensionMaximisingAnnuity({ assessableAssets: counted, isCouple, homeowner: i.homeowner, investable: i.investable, ages: [clientAge, partnerAge] });
    const ceiling = annuityCeilingOf(factFind as never, i.capPct);
    const pct = ceiling.pct ?? i.capPct;
    const capped = ceiling.amount != null ? Math.min(i.investable, Math.round(ceiling.amount)) : Math.round(i.investable * (Math.min(100, pct) / 100));
    return annuityTestAmounts(i.investable, pct, pensionMax, ceiling.amount).map((amount) => ({
        amount,
        pensionMax: pensionMax != null && amount === pensionMax && amount !== capped,
        sharePct: Math.round((amount / i.investable) * 100),
    }));
}

/** The plain words for a size, as plan names use them: "$250,000, the amount that lifts the Age Pension most". */
export function annuitySizeWords(size: AnnuitySize, money: (n: number) => string): string {
    return size.pensionMax ? `${money(size.amount)}, the amount that lifts the Age Pension most` : money(size.amount);
}
